import { useState } from 'react';
import {
  Typography,
  Table,
  Input,
  Select,
  Space,
  DatePicker,
  Row,
  Col,
  Button,
  Tag,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useAuditLogs } from '@hooks/useAdmin';
import type { AuditLogItem, AuditLogFilters } from '@/types/admin';

const { RangePicker } = DatePicker;

const mdActionColors: Record<string, string> = {
  Create: 'green',
  Update: 'blue',
  Delete: 'red',
  SoftDelete: 'red',
};

const mdEntityOptions = [
  { label: 'Product', value: 'Product' },
  { label: 'ProductBatch', value: 'ProductBatch' },
  { label: 'PurchaseOrder', value: 'PurchaseOrder' },
  { label: 'SalesOrder', value: 'SalesOrder' },
  { label: 'Customer', value: 'Customer' },
  { label: 'Supplier', value: 'Supplier' },
  { label: 'Invoice', value: 'Invoice' },
  { label: 'ServiceRequest', value: 'ServiceRequest' },
  { label: 'User', value: 'User' },
  { label: 'Recall', value: 'Recall' },
];

export default function AuditLogPage() {
  const [mdFilters, setMdFilters] = useState<AuditLogFilters>({
    page: 1,
    pageSize: 25,
  });
  const [mdSearchText, setMdSearchText] = useState('');
  const [mdDateRange, setMdDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  const { data: mdAuditData, isLoading: mdIsLoading } = useAuditLogs(mdFilters);

  const mdHandleSearch = () => {
    setMdFilters((mdPrev) => ({ ...mdPrev, search: mdSearchText || undefined, page: 1 }));
  };

  const mdHandleEntityFilter = (mdEntity: string | undefined) => {
    setMdFilters((mdPrev) => ({ ...mdPrev, entityName: mdEntity, page: 1 }));
  };

  const mdHandleDateChange = (mdDates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    setMdDateRange(mdDates);
    const mdFrom = mdDates?.[0]?.startOf('day').toISOString();
    const mdTo = mdDates?.[1]?.endOf('day').toISOString();
    setMdFilters((mdPrev) => ({
      ...mdPrev,
      dateFrom: mdFrom ?? undefined,
      dateTo: mdTo ?? undefined,
      page: 1,
    }));
  };

  const mdGetRowClassName = (mdRecord: AuditLogItem): string => {
    if (mdRecord.action === 'Create') return 'audit-row-create';
    if (mdRecord.action === 'Delete' || mdRecord.action === 'SoftDelete') return 'audit-row-delete';
    return '';
  };

  const mdColumns: ColumnsType<AuditLogItem> = [
    {
      title: 'Час',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (mdTs: string) => dayjs(mdTs).format('DD.MM.YYYY HH:mm:ss'),
    },
    {
      title: 'Користувач',
      dataIndex: 'userName',
      key: 'userName',
      width: 140,
      render: (mdName: string | undefined) => mdName ?? '—',
    },
    {
      title: 'Сутність',
      dataIndex: 'entityName',
      key: 'entityName',
      width: 140,
    },
    {
      title: 'ID',
      dataIndex: 'entityId',
      key: 'entityId',
      width: 120,
      ellipsis: true,
      render: (mdId: string | undefined) => mdId ?? '—',
    },
    {
      title: 'Дія',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (mdAction: string) => (
        <Tag color={mdActionColors[mdAction] ?? 'default'}>{mdAction}</Tag>
      ),
    },
    {
      title: 'Поле',
      dataIndex: 'propertyName',
      key: 'propertyName',
      width: 140,
      render: (mdVal: string | undefined) => mdVal ?? '—',
    },
    {
      title: 'Старе значення',
      dataIndex: 'oldValue',
      key: 'oldValue',
      width: 180,
      ellipsis: true,
      render: (mdVal: string | undefined) => mdVal ?? '—',
    },
    {
      title: 'Нове значення',
      dataIndex: 'newValue',
      key: 'newValue',
      width: 180,
      ellipsis: true,
      render: (mdVal: string | undefined) => mdVal ?? '—',
    },
    {
      title: 'IP',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 130,
      render: (mdIp: string | undefined) => mdIp ?? '—',
    },
  ];

  return (
    <>
      <style>{`
        .audit-row-create { background-color: #f6ffed; }
        .audit-row-delete { background-color: #fff2f0; }
      `}</style>

      <Typography.Title level={4} style={{ marginBottom: 16 }}>
        Журнал аудиту
      </Typography.Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Input
              placeholder="Пошук..."
              prefix={<SearchOutlined />}
              value={mdSearchText}
              onChange={(mdE) => setMdSearchText(mdE.target.value)}
              onPressEnter={mdHandleSearch}
              style={{ width: 250 }}
              allowClear
            />
            <Select
              placeholder="Сутність"
              allowClear
              style={{ width: 180 }}
              onChange={mdHandleEntityFilter}
              options={mdEntityOptions}
            />
            <RangePicker
              value={mdDateRange}
              onChange={mdHandleDateChange}
              format="DD.MM.YYYY"
              placeholder={['Від', 'До']}
            />
            <Button onClick={mdHandleSearch}>Шукати</Button>
          </Space>
        </Col>
      </Row>

      <Table<AuditLogItem>
        rowKey="id"
        columns={mdColumns}
        dataSource={mdAuditData?.items ?? []}
        loading={mdIsLoading}
        rowClassName={mdGetRowClassName}
        pagination={{
          current: mdFilters.page,
          pageSize: mdFilters.pageSize,
          total: mdAuditData?.totalCount ?? 0,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          onChange: (mdPage, mdSize) =>
            setMdFilters((mdPrev) => ({ ...mdPrev, page: mdPage, pageSize: mdSize })),
        }}
        scroll={{ x: 1400 }}
        size="middle"
      />
    </>
  );
}
